// competition-bands.js — Tab 2: Band list + band detail subview.

import { apiFetch } from './utils.js';

// ── Band list ────────────────────────────────────────────────────────────────

export async function renderBandsTab() {
  return `
    <div class="bands-controls" style="display:flex;gap:.75rem;align-items:center;margin-bottom:1rem;flex-wrap:wrap">
      <input type="text" id="band-search" class="form-input" placeholder="Search bands…"
        style="width:220px" oninput="window.filterBandsList()">
      <label style="display:flex;align-items:center;gap:.4rem;font-size:.82rem;color:var(--text-muted);cursor:pointer">
        <input type="checkbox" id="band-filter-clients" onchange="window.filterBandsList()">
        WMM clients only
      </label>
    </div>
    <div id="bands-list"><div class="loading-state">Loading bands…</div></div>
  `;
}

export async function loadBandsList() {
  const el = document.getElementById('bands-list');
  if (!el) return;
  try {
    const bands = await apiFetch('/api/competition-analysis/bands?limit=500');
    window._compBandsCache = bands;
    renderBandsListUI(bands);
  } catch (err) {
    el.innerHTML = `<div class="error-state">Failed to load bands: ${err.message}</div>`;
  }
}

function renderBandsListUI(bands) {
  const el = document.getElementById('bands-list');
  if (!el) return;
  if (!bands.length) {
    el.innerHTML = '<div class="empty-state">No bands found.</div>';
    return;
  }
  el.innerHTML = `
    <table class="schedule-table">
      <thead>
        <tr>
          <th>Band</th><th>State</th><th>Events</th><th>Seasons</th><th>WMM Client</th>
        </tr>
      </thead>
      <tbody>
        ${bands.map(b => `
          <tr class="schedule-row" onclick="window.openBandDetail(${b.id})" style="cursor:pointer">
            <td style="font-weight:500">${b.name}</td>
            <td>${b.state || '—'}</td>
            <td>${b.event_count || 0}</td>
            <td>${b.first_season && b.last_season
                  ? (b.first_season === b.last_season ? b.first_season : `${b.first_season}–${b.last_season}`)
                  : '—'}</td>
            <td>${b.client_id
                  ? '<span style="color:#4caf50;font-size:.8rem">✓ linked</span>'
                  : '<span style="color:#5a7a9a;font-size:.8rem">—</span>'}</td>
          </tr>`).join('')}
      </tbody>
    </table>`;
}

// ── Band detail subview ──────────────────────────────────────────────────────

export async function renderBandDetail(bandId) {
  const el = document.getElementById('comp-tab-content');
  if (!el) return;
  el.innerHTML = '<div class="loading-state">Loading band…</div>';

  try {
    const data = await apiFetch(`/api/competition-analysis/band/${bandId}`);
    const b = data.band;
    const seasons = data.seasons || [];

    el.innerHTML = `
      <div style="margin-bottom:1rem;display:flex;align-items:center;gap:.75rem">
        <button class="btn-secondary" onclick="window.backToBandsList()" style="font-size:.82rem">← Bands</button>
        <div>
          <div style="font-size:1.05rem;font-weight:600">${b.name}</div>
          <div style="font-size:.8rem;color:var(--text-muted)">${[b.school, b.city, b.state].filter(Boolean).join(' · ')}</div>
        </div>
        ${!b.client_id ? `
          <button class="btn-add-inline" style="margin-left:auto" onclick="window.openBandLinkModal(${b.id})">Link WMM Client</button>
        ` : `<span style="margin-left:auto;font-size:.82rem;color:#4caf50">✓ WMM client</span>`}
      </div>

      ${seasons.length === 0
        ? '<div class="empty-state">No competition entries found.</div>'
        : seasons.map(s => renderBandSeasonSection(s)).join('')}

      ${renderBandLinkModal()}
    `;
  } catch (err) {
    el.innerHTML = `<div class="error-state">Failed to load band: ${err.message}</div>`;
  }
}

function renderBandSeasonSection(season) {
  return `
    <div style="margin-bottom:1.5rem">
      <div style="font-size:.85rem;font-weight:600;color:var(--text-muted);margin-bottom:.5rem;text-transform:uppercase;letter-spacing:.05em">
        ${season.season} Season · ${season.entries.length} shows
      </div>
      <table class="schedule-table" style="font-size:.83rem">
        <thead>
          <tr><th>Date</th><th>Competition</th><th>Place</th><th>Score</th><th>Class</th></tr>
        </thead>
        <tbody>
          ${season.entries.map(e => `
            <tr class="schedule-row" onclick="window.openCompSheetFromBand(${e.competition_id})" style="cursor:pointer">
              <td>${e.event_date ? e.event_date.slice(0,10) : '—'}</td>
              <td>${e.competition_name}</td>
              <td>${e.placement ? `#${e.placement}` : '—'}</td>
              <td>${e.total_score ?? '—'}</td>
              <td>${e.classification || '—'}</td>
            </tr>`).join('')}
        </tbody>
      </table>

      ${season.caption_summary && season.caption_summary.length ? `
        <details style="margin-top:.5rem">
          <summary style="font-size:.78rem;color:var(--text-muted);cursor:pointer">Caption summary (latest show)</summary>
          <div style="margin-top:.4rem;display:flex;flex-wrap:wrap;gap:.4rem">
            ${season.caption_summary.map(cs => {
              const status = window.fmtBookStatus ? window.fmtBookStatus(cs.sub1, cs.sub2) : '';
              return `<span style="font-size:.78rem;padding:.2rem .5rem;background:rgba(255,255,255,.05);border-radius:4px">
                ${cs.caption_group}: ${cs.total ?? '—'} ${status}
              </span>`;
            }).join('')}
          </div>
        </details>` : ''}
    </div>`;
}

function renderBandLinkModal() {
  return `
    <div id="band-link-modal" class="modal-overlay hidden">
      <div class="modal-dialog" style="max-width:480px">
        <div class="modal-header">
          <span>Link to WMM Client</span>
          <button class="modal-close-btn" onclick="document.getElementById('band-link-modal').classList.add('hidden')">×</button>
        </div>
        <div class="modal-body">
          <div class="form-field">
            <label>WMM Client ID</label>
            <input type="number" id="band-link-client-id" class="form-input" placeholder="Client ID from CRM">
          </div>
          <div style="font-size:.8rem;color:var(--text-muted);margin-top:.25rem">
            Enter the client ID from the CRM. This links all competition data for this band to that client.
          </div>
        </div>
        <div class="modal-footer">
          <button onclick="window.saveBandLink()" class="btn-primary">Link Client</button>
        </div>
      </div>
    </div>`;
}

// ── Window handlers ──────────────────────────────────────────────────────────

export function initBandsHandlers() {
  window.filterBandsList = function() {
    const q        = document.getElementById('band-search')?.value.toLowerCase() || '';
    const clientsOnly = document.getElementById('band-filter-clients')?.checked;
    const bands    = window._compBandsCache || [];
    const filtered = bands.filter(b => {
      if (clientsOnly && !b.client_id) return false;
      if (q && !b.name.toLowerCase().includes(q)) return false;
      return true;
    });
    renderBandsListUI(filtered);
  };

  window.openBandDetail = function(bandId) {
    window._compPrevBandsList = true;
    renderBandDetail(bandId);
  };

  window.backToBandsList = async function() {
    const el = document.getElementById('comp-tab-content');
    if (!el) return;
    el.innerHTML = await renderBandsTab();
    await loadBandsList();
  };

  window.openCompSheetFromBand = function(competitionId) {
    // Switch to Results tab with this competition pre-selected
    window._compPreselectedComp = competitionId;
    document.querySelectorAll('.comp-tab-btn').forEach(b => b.classList.remove('active'));
    const resultsBtn = document.querySelector('.comp-tab-btn[data-tab="results"]');
    if (resultsBtn) resultsBtn.classList.add('active');
    window.loadCompTab('results');
  };

  window.openBandLinkModal = function(bandId) {
    window._linkBandId = bandId;
    document.getElementById('band-link-modal')?.classList.remove('hidden');
  };

  window.saveBandLink = async function() {
    const clientId = parseInt(document.getElementById('band-link-client-id')?.value);
    if (!clientId || !window._linkBandId) { alert('Client ID is required'); return; }
    try {
      await apiFetch(`/api/competition/bands/${window._linkBandId}/link-client`, {
        method: 'POST', body: JSON.stringify({ client_id: clientId })
      });
      document.getElementById('band-link-modal')?.classList.add('hidden');
      renderBandDetail(window._linkBandId);
    } catch (err) {
      alert('Failed to link client: ' + err.message);
    }
  };
}
